
import { defineComponent, ref } from "vue";
import {
  required,
  maxLength,
  helpers,
  sameAs,
  minLength,
  email,
} from "@vuelidate/validators";
import { useVuelidate } from "@vuelidate/core";
import ToolTip from "@/components/common/ToolTip.vue";
import routesPath from "@/config/routesPath";
import * as apiClient from "@/apiclient/";
import TokenService from "@/services/tokenService";
import messages from "@/config/messages";
import common from "@/config/common";
import { mapGetters } from "vuex";

interface UserData {
  ssn: string;
  zip: string;
  dob: string;
  lastName: string;
  fullName: string;
  userName: string;
  password: string;
  passwordConfirmation: string;
  email: string;
  securityQuestion: string;
  securityAnswer: string;
}

export default defineComponent({
  name: "ResetPassword",
  components: {
    ToolTip,
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      user: {
        password: "",
        passwordConfirmation: "",
      } as UserData,

      submitted: false,
      isFormInvalid: false,
      validationMsg: "",
      isSubmitDisabled: false,
      messages,
      routesPath,
      step: 1,
      passwordReset: false,
    };
  },
  validations() {
    return {
      user: {
        password: {
          ...{
            required,
            differentThanSecurityQuestionAnswerValidation: helpers.withMessage(
              "Password cannot be the same as the security answer",
              () => {
                const { password, securityAnswer } = this.user;
                return (
                  password === "" ||
                  securityAnswer === "" ||
                  password !== securityAnswer
                );
              }
            ),
            passwordCannotContainUsernameValidation: helpers.withMessage(
              "Password cannot contain your username",
              () => {
                const { userName, password } = this.user;
                return (
                  userName === "" ||
                  password === "" ||
                  !password.includes(userName)
                );
              }
            ),
            valid: helpers.withMessage(
              "Please enter a strong password (One lower, one upper, one number, one special character, and at least 10 characters long)",
              (value: string) => {
                if (this.user.password === "") return true;
                const containsUppercase = /[A-Z]/.test(value);
                const containsLowercase = /[a-z]/.test(value);
                const containsNumber = /[0-9]/.test(value);
                const containsSpecial = /[#?!@$%^&*-]/.test(value);
                return (
                  containsUppercase &&
                  containsLowercase &&
                  containsNumber &&
                  containsSpecial
                );
              }
            ),
            sameAsPassword: helpers.withMessage(
              "Password does not match",
              sameAs(this.user.passwordConfirmation)
            ),
            minLength: minLength(10),
            maxLength: maxLength(128),
          },
        },
        passwordConfirmation: {
          ...{
            required,
            sameAsPassword: helpers.withMessage(
              "Password does not match",
              sameAs(this.user.password)
            ),
          },
        },
      },
    };
  },
  mounted() {
    var token = TokenService.getToken("ResetPasswordToken");
    if (token?.length == 0) {
      this.$router.push({
        path: routesPath.base,
      });
    }
  },
  computed: {
    ...mapGetters(["getAutoLogoutMsg"]),
  },
  methods: {
    goToLogin() {
      this.$router.push("/");
    },
    hideErrors(field: string): void {
      this.v$.user[field].$touch();
      this.isFormInvalid = false;
    },
    backButtonHandler() {
      this.isFormInvalid = false;
      this.$router.push({
        path: routesPath.base,
      });
    },
    buttonDesign(): string {
      const buttonStatus = this.v$.user.$invalid;

      return buttonStatus == true ? "btn-secondary w-80" : "btn-primary w-80";
    },
    async handleSubmit() {
      this.reset();
    },
    async reset(): Promise<void> {
      this.isFormInvalid = false;
      this.submitted = true;
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      }

      var params = {
        password: this.user.password,
      };
      this.isSubmitDisabled = true;
      const result = await apiClient
        .forgotController()
        .postApiForgotResetpassword(params, {
          headers: {
            Authorization:
              "Bearer " + TokenService.getToken("ResetPasswordToken"),
          },
        });

      this.isSubmitDisabled = false;

      if (!result || result.error) {
        this.isFormInvalid = true;
        if (result.error) {
          this.validationMsg = result.error + messages.errorMsg;
        }
        return;
      } else if (result.data?.passwordResetCompleted) {
        this.passwordReset = true;
        this.step = 0;
        return;
      } else {
        this.isFormInvalid = true;
        const notCreatedMessages: string[] = result.data?.messages || [];
        notCreatedMessages.forEach((msg) => {
          this.validationMsg = this.validationMsg + msg;
        });
        return;
      }
    },
  },
});
